/**
 * StoicOs.ai — Admin dashboard translations.
 *
 * Keys are prefixed with `admin.` so they can be merged into the main
 * translations dictionary without collision. Every new admin page should
 * import strings via `t("admin.<page>.<field>")` — never hard-code UI text.
 *
 * Translation rules:
 *   - Natural Dutch (not literal). Address the user as "u" (formal).
 *   - Keep product names untranslated: StoicOs.ai, Virtue Guide, AutoSEO, Resend,
 *     App Store, Google Play, MongoDB, FastAPI, GA4.
 *   - Keep the phrase "Command Center" untranslated per Ed's preference.
 */
export const adminTranslations = {
  en: {
    // ---------- Common / actions ----------
    "admin.action.edit": "Edit",
    "admin.action.delete": "Delete",
    "admin.action.publish": "Publish",
    "admin.action.unpublish": "Unpublish",
    "admin.action.preview": "Preview",
    "admin.action.save": "Save",
    "admin.action.cancel": "Cancel",
    "admin.action.saveDraft": "Save Draft",
    "admin.action.update": "Update Article",
    "admin.action.create": "Create Article",
    "admin.action.new": "New",
    "admin.action.search": "Search",
    "admin.action.filter": "Filter",
    "admin.action.open": "Open",
    "admin.action.close": "Close",
    "admin.action.refresh": "Refresh",
    "admin.action.copy": "Copy",
    "admin.action.reveal": "Reveal",
    "admin.action.hide": "Hide",
    "admin.action.testConnection": "Test Connection",
    "admin.action.markAllRead": "Mark all read",
    "admin.action.viewAll": "View all",
    "admin.action.viewAllArticles": "View all articles",
    "admin.action.retest": "Re-run health checks",
    "admin.action.enter": "Enter Command Center",

    // ---------- Common labels / status ----------
    "admin.label.loading": "Loading…",
    "admin.label.saving": "Saving…",
    "admin.label.verifying": "Verifying…",
    "admin.label.total": "Total",
    "admin.label.recent": "Recent",
    "admin.label.recentActivity": "Recent Activity",
    "admin.label.recentArticles": "Recent Articles",
    "admin.label.published": "Published",
    "admin.label.draft": "Draft",
    "admin.label.drafts": "Drafts",
    "admin.label.status": "Status",
    "admin.label.online": "Online",
    "admin.label.offline": "Offline",
    "admin.label.connected": "Connected",
    "admin.label.disconnected": "Disconnected",
    "admin.label.configured": "Configured",
    "admin.label.notSet": "Not set",
    "admin.label.nominal": "Nominal",
    "admin.label.on": "On",
    "admin.label.off": "Off",
    "admin.label.ok": "OK",
    "admin.label.down": "Down",
    "admin.label.inDevelopment": "In Development",
    "admin.label.comingSoon": "Coming Soon",
    "admin.label.placeholder": "Placeholder",
    "admin.label.private": "Private",
    "admin.label.founderOnlyAccess": "Founder-only access · No public registration",
    "admin.label.authorised": "Authorised personnel only",
    "admin.label.live": "Live",
    "admin.label.lifetime": "lifetime",
    "admin.label.rollingWeek": "Rolling week",
    "admin.label.last7d": "Last 7 Days",
    "admin.journal.section.published": "Published",
    "admin.journal.section.drafts": "Drafts",
    "admin.label.today": "Today",
    "admin.label.yesterday": "Yesterday",
    "admin.label.language": "Language",

    // ---------- Sidebar / navigation ----------
    "admin.nav.dashboard": "Dashboard",
    "admin.nav.publishingStudio": "Publishing Studio",
    "admin.nav.journal": "Journal",
    "admin.nav.editorial": "Editorial Dashboard",
    "admin.nav.contentCalendar": "Content Calendar",
    "admin.nav.newsletter": "Newsletter",
    "admin.nav.seoCenter": "SEO Center",
    "admin.nav.aiJobs": "AI Job Monitor",
    "admin.nav.group.content": "Content",
    "admin.nav.group.academy": "Academy",
    "admin.nav.group.media": "Media",
    "admin.nav.group.marketing": "Marketing",
    "admin.nav.group.system": "System",
    "admin.nav.lexicon": "Lexicon",
    "admin.nav.vault": "Vault",
    "admin.nav.siteMedia": "Site Media",
    "admin.nav.students": "Students",
    "admin.nav.dailyInspiration": "Daily Inspiration",
    "admin.nav.founders": "Founders Wall",
    "admin.founders.title": "Founder Portal",
    "admin.founders.subtitle": "Founding Members are ONLY added through explicit confirmation below — never automatically via registration, test accounts or database presence. Full data is only visible here.",
    "admin.founders.confirmTitle": "Confirm Founding Member",
    "admin.founders.confirmHint": "Enter the account email of the person you explicitly confirm as Founding Member. This is the only way onto the Wall.",
    "admin.founders.confirmPlaceholder": "member@email.com",
    "admin.founders.confirmBtn": "Confirm",
    "admin.founders.confirmed": "Confirmed",
    "admin.founders.alreadyConfirmed": "This person is already a confirmed Founding Member.",
    "admin.founders.revoke": "Revoke",
    "admin.founders.revokeConfirm": "Remove this person from the Founders Wall entirely? The numbering will close the gap automatically.",
    "admin.founders.editName": "Edit public Wall name (does not change the account profile)",
    "admin.founders.editNamePrompt": "Public display name (format: first name + surname initial, e.g. Daniel M.). Leave empty to derive from the account name.",
    "admin.founders.count": "Founding Members",
    "admin.founders.remaining": "Places remaining",
    "admin.founders.onWall": "Visible on the Wall",
    "admin.founders.pending": "Awaiting choice",
    "admin.founders.colNumber": "No.",
    "admin.founders.colPublic": "Public name on the Wall",
    "admin.founders.publicNamePlaceholder": "e.g. Saskia L.",
    "admin.founders.publicNameHint": "Only changes how the name appears on the public Founders Wall (format: first name + surname initial). Account name, email and profile stay untouched. The member number never changes.",
    "admin.founders.colName": "Full name",
    "admin.founders.colEmail": "Email",
    "admin.founders.colDate": "Registered",
    "admin.founders.colPref": "Preference",
    "admin.founders.colActions": "Actions",
    "admin.founders.show": "Show on wall",
    "admin.founders.hide": "Hide from wall",
    "admin.founders.remove": "Remove",
    "admin.founders.restore": "Restore",
    "admin.founders.removed": "Removed",
    "admin.founders.prefYes": "Yes",
    "admin.founders.prefNo": "No",
    "admin.founders.prefPending": "Pending",
    "admin.students.title": "Students",
    "admin.students.subtitle": "Manage student accounts and onboarding status.",
    "admin.students.search": "Search by name or email…",
    "admin.students.done": "Completed",
    "admin.students.pending": "Not completed",
    "admin.students.reset": "Reset onboarding",
    "admin.students.resetConfirm": "Reset onboarding for {name}? They will see the full onboarding ceremony again at next login.",
    "admin.students.empty": "No students found.",
    "admin.nav.logicMedia": "Logic Media",
    "admin.nav.logicCms": "Logic CMS",
    "admin.nav.physicsMedia": "Physics Media",
    "admin.nav.physicsCms": "Physics CMS",
    "admin.nav.ethicsMedia": "Ethics Media",
    "admin.nav.ethicsCms": "Ethics CMS",
    "admin.nav.library": "Library",
    "admin.nav.autoseo": "AutoSEO",
    "admin.nav.analytics": "Analytics",
    "admin.nav.downloads": "Downloads",
    "admin.nav.messages": "Messages",
    "admin.nav.settings": "Settings",
    "admin.nav.system": "System",
    "admin.nav.logout": "Logout",
    "admin.nav.backHome": "StoicOs.ai Home",
    "admin.brand": "StoicOs.ai · Command Center",

    // ---------- Login (Founder Portal) ----------
    "admin.login.brand": "StoicOs.ai",
    "admin.login.title": "Founder Portal",
    "admin.login.subtitle": "Private access to the StoicOs.ai Command Center.",
    "admin.login.tokenLabel": "Access Token",
    "admin.login.tokenPlaceholder": "Enter your token",
    "admin.login.invalid": "Invalid token.",
    "admin.login.networkError": "Network error.",
    "admin.login.serverError": "Server error ({status}). Try again.",

    // ---------- Dashboard ----------
    "admin.dash.welcome": "Welcome back, Ed.",
    "admin.dash.welcomeSub": "Your private StoicOs.ai Command Center.",
    "admin.dash.quickActions": "Quick Actions",
    "admin.dash.newArticle": "New Journal Article",
    "admin.dash.openJournal": "Open Journal",
    "admin.dash.openEditorial": "Editorial Dashboard",
    "admin.dash.openDashboard": "Open Dashboard",
    "admin.dash.daily": "Daily Command Center",
    "admin.dash.stat.drafts": "Drafts",
    "admin.dash.stat.drafts.hint": "Waiting for review",
    "admin.dash.stat.published": "Published",
    "admin.dash.stat.published.hint": "Live on site",
    "admin.dash.stat.total": "Total Articles",
    "admin.dash.stat.total.hint": "Journal library",
    "admin.dash.stat.messages": "New Messages",
    "admin.dash.stat.visitors": "Visitors Today",
    "admin.dash.stat.visitors.hint": "Wire in GA4",
    "admin.dash.stat.downloads": "VG Downloads",
    "admin.dash.stat.autoseo": "AutoSEO",
    "admin.dash.stat.autoseo.noImports": "No imports yet",
    "admin.dash.stat.autoseo.last": "Last: {date}",
    "admin.dash.stat.system": "System",
    "admin.dash.stat.system.hint": "{count} on waitlist",
    "admin.dash.noArticles": "No articles yet. Create one from the Journal.",

    // ---------- Journal admin ----------
    "admin.journal.title": "Journal Admin",
    "admin.journal.backToDash": "Back to Dashboard",
    "admin.journal.editor.new": "New article",
    "admin.journal.editor.edit": "Edit article",
    "admin.journal.editor.title": "Title",
    "admin.journal.editor.excerpt": "Excerpt",
    "admin.journal.editor.slug": "Slug",
    "admin.journal.editor.body": "Body",
    "admin.journal.editor.tags": "Tags",
    "admin.journal.editor.category": "Category",
    "admin.journal.editor.cover": "Featured Image",
    "admin.journal.editor.pickVault": "Choose from Content Vault",
    "admin.journal.editor.seoTitle": "SEO Title",
    "admin.journal.editor.seoDescription": "SEO Description",
    "admin.journal.editor.featured": "Featured",
    "admin.journal.editor.author": "Author",
    "admin.journal.msg.editing": "Now editing: {title}",
    "admin.journal.localization.title":     "Localization health",
    "admin.journal.localization.loading":   "Analysing published articles\u2026",
    "admin.journal.localization.healthy":   "All {total} published articles are fully bilingual.",
    "admin.journal.localization.summary":   "{missing} of {total} published articles are missing Dutch fields and will fall back to English on stoicos.ai/nl.",
    "admin.journal.localization.fixAll":    "Translate missing NL",
    "admin.journal.localization.refresh":   "Refresh",
    "admin.journal.localization.expand":    "Details",
    "admin.journal.localization.collapse":  "Hide",
    "admin.journal.localization.col.article": "Article",
    "admin.journal.localization.col.reason":  "Root cause",
    "admin.journal.localization.col.fix":     "Fix",
    "admin.journal.localization.col.retranslate": "Retranslate",
    "admin.journal.list.drafts": "Drafts",
    "admin.journal.list.published": "Published",
    "admin.journal.list.empty": "No articles yet.",
    "admin.journal.trans.both": "EN + NL",
    "admin.journal.trans.en_only": "EN only",
    "admin.journal.trans.nl_only": "NL only",
    "admin.journal.trans.pending_nl": "Pending NL",
    "admin.journal.trans.failed_nl": "Failed NL",
    "admin.journal.trans.none": "no title",
    "admin.journal.trans.tooltip": "Bilingual translation status. Both EN and NL should be filled before Dutch visitors can read this article natively.",
    "admin.journal.trans.filter.all": "All articles",
    "admin.journal.trans.filter.missing_nl": "Missing NL",
    "admin.journal.trans.filter.missing_en": "Missing EN",
    "admin.journal.trans.filter.label": "Translation",
    "admin.journal.trans.warn.title": "Dutch translation is missing",
    "admin.journal.trans.warn.body": "This article will be published in English only. Dutch visitors will silently see the English version until you add the NL fields below.",
    "admin.journal.trans.warn.title_en": "English translation is missing",
    "admin.journal.trans.warn.body_en": "This article has no English content. Add the EN fields above, otherwise English visitors will see the Dutch text as fallback.",
    "admin.journal.trans.copy": "Copy from EN",
    "admin.journal.trans.copied": "Copied EN → NL",
    "admin.journal.trans.ai.button": "Translate with AI",
    "admin.journal.trans.ai.working": "Translating with Claude. Long articles can take a few minutes — keep this window open…",
    "admin.journal.trans.ai.working_short": "Translating…",
    "admin.journal.trans.ai.done": "Dutch draft ready. Review below, then Save.",
    "admin.journal.trans.ai.needSave": "Save the article first, then click Translate.",
    "admin.journal.trans.ai.failed": "Translation failed. Nothing was changed on the article. Please try again.",
    "admin.journal.trans.ai.stillRunning": "The translation is still running in the background. Nothing has been changed yet — try again in a minute.",
    "admin.journal.approval.title": "Founder Approval",
    "admin.journal.approval.en": "Approve English version",
    "admin.journal.approval.nl": "Approve Dutch version",
    "admin.journal.approval.required": "Approval required",
    "admin.journal.approval.ready": "Ready to publish",
    "admin.journal.approval.saveFirst": "Save the article first, then approve both language versions.",
    "admin.journal.approval.blocked": "This article cannot be published yet: approve both the English and Dutch versions first in the Founder Approval section.",
    "admin.journal.cleanup.title": "AutoSEO publication check",
    "admin.journal.cleanup.run": "Run cleanup protocol",
    "admin.journal.cleanup.notRun": "The mandatory cleanup protocol has not been run on this article yet. Run it to remove external links, referral text, AI remnants and placeholders.",
    "admin.journal.cleanup.passed": "Passed",
    "admin.journal.cleanup.failed": "Failed",
    "admin.journal.cleanup.yes": "yes",
    "admin.journal.cleanup.no": "no",
    "admin.journal.cleanup.removed": "removed",
    "admin.journal.cleanup.extLinks": "External links removed",
    "admin.journal.cleanup.refText": "Referral text removed",
    "admin.journal.cleanup.aiRemnants": "AI remnants removed",
    "admin.journal.cleanup.placeholders": "Placeholders removed",
    "admin.journal.cleanup.internal": "Internal StoicOs links checked",
    "admin.journal.cleanup.nlClean": "Dutch version clean",
    "admin.journal.cleanup.enClean": "English version clean",
    "admin.journal.cleanup.failures": "Remaining passages (must be fixed)",
    "admin.journal.cleanup.reviewFlags": "Editorial warnings — confirm explicitly before publishing",
    "admin.journal.cleanup.blocked": "Publishing is locked until the cleanup protocol passes for every language.",
    "admin.journal.cleanup.msgPassed": "Cleanup protocol passed — article is clean.",
    "admin.journal.cleanup.msgFailed": "Cleanup ran, but issues remain. Review the passages below.",
    "admin.journal.warnings.title": "Editorial warnings",
    "admin.journal.warnings.intro": "The following passages were flagged for editorial review (promotional terms, name mentions or unverified topical claims). Nothing was removed automatically — review each passage and confirm explicitly before publishing.",
    "admin.journal.warnings.open": "Awaiting confirmation",
    "admin.journal.warnings.confirmed": "Confirmed",
    "admin.journal.warnings.pending": "Confirm warnings first",
    "admin.journal.warnings.ackLabel": "I have reviewed these editorial warnings and explicitly confirm this article may be published.",
    "admin.journal.warnings.alreadyAck": "Previously confirmed by the founder.",
    "admin.journal.warnings.blocked": "Publishing is locked until the editorial warnings are explicitly confirmed.",
    "admin.journal.warnings.promo": "Promotional term",
    "admin.journal.warnings.named": "Name mention (verify)",
    "admin.journal.warnings.claim": "Unverified topical claim",
    "admin.journal.bulk.title": "{count} article(s) need Dutch",
    "admin.journal.bulk.body": "Translate every article whose title, excerpt, body or SEO description is still missing in Dutch. Each translation is saved as a draft. You review before publishing.",
    "admin.journal.bulk.button": "Bulk translate missing NL",
    "admin.journal.bulk.modal.eyebrow": "AI Bulk Translation",
    "admin.journal.bulk.modal.title": "Translate {count} article(s) to Dutch",
    "admin.journal.bulk.modal.start": "Start translation",
    "admin.journal.bulk.modal.retry": "Retry failed",
    "admin.journal.bulk.modal.cancel": "Cancel",
    "admin.journal.bulk.modal.close": "Close",
    "admin.journal.bulk.modal.progress": "{done} of {total} done",
    "admin.journal.bulk.copyError": "Copy",
    "admin.journal.bulk.errorKind": "{kind}",

    // ---------- AutoSEO ----------
    "admin.autoseo.title": "AutoSEO",
    "admin.autoseo.intro": "The AutoSEO webhook receives generated articles from your pipeline and creates them as drafts inside the Journal. Nothing is auto-published.",
    "admin.autoseo.webhookStatus": "Webhook Status",
    "admin.autoseo.bearerStatus": "Bearer Token",
    "admin.autoseo.articlesWaiting": "Articles Waiting",
    "admin.autoseo.totalImported": "Total Imported",
    "admin.autoseo.connection": "Connection",
    "admin.autoseo.endpoint": "Webhook Endpoint",
    "admin.autoseo.lastImport": "Last Imported Article",
    "admin.autoseo.noImports": "No imports yet.",
    "admin.autoseo.openLogs": "Open AutoSEO Logs",
    "admin.autoseo.history": "Import History",
    "admin.autoseo.historyEmpty": "No AutoSEO imports yet.",
    "admin.autoseo.scheduled": "Scheduled Imports",
    "admin.autoseo.scheduledHint": "Recurring & scheduled imports will appear here once the AutoSEO pipeline supports cron-based publishing.",
    "admin.autoseo.accepting": "Accepting POST",
    "admin.autoseo.tokenMissing": "Token not set",

    // ---------- Analytics ----------
    "admin.analytics.title": "Analytics",
    "admin.analytics.intro": "Live data connects as each integration is wired. Traffic breakdown is a placeholder until GA4 metrics are proxied server-side.",
    "admin.analytics.visitors": "Website Visitors",
    "admin.analytics.journalViews": "Journal Views",
    "admin.analytics.downloads": "Downloads",
    "admin.analytics.conversion": "Conversion Rate",
    "admin.analytics.conversion.hint": "Waitlist → download",
    "admin.analytics.articlesLive": "Articles Live",
    "admin.analytics.readingTime": "Avg. Reading Time",
    "admin.analytics.returnVisitors": "Return Visitors",
    "admin.analytics.waitlist": "Waitlist",
    "admin.analytics.waitlist.hint": "Total subscribers",
    "admin.analytics.traffic": "Traffic Sources",
    "admin.analytics.trafficNote": "Sample distribution · Wire GA4 for live data",
    "admin.analytics.topArticles": "Most Viewed Articles",

    // ---------- Library ----------
    "admin.library.title": "Library",
    "admin.libcms.title": "Living Library CMS",
    "admin.libcms.intro": "Founder publishing workflow for the Wisdom Journeys. Draft → In Review → Published. Nothing goes live without your approval. Publishing activates the Compass Rose icon automatically.",
    "admin.libcms.empty": "No Journeys yet.",
    "admin.libcms.status.draft": "Draft",
    "admin.libcms.status.review": "In Review",
    "admin.libcms.status.published": "Published",
    "admin.libcms.status.archived": "Archived",
    "admin.libcms.hero": "Hero",
    "admin.libcms.lexicon": "Lexicon",
    "admin.libcms.reflection": "Reflection",
    "admin.libcms.practice": "Practice",
    "admin.libcms.publishedAt": "Published",
    "admin.libcms.modified": "Last modified",
    "admin.libcms.versions": "Versions",
    "admin.libcms.sync": "Synchronize Journey Content",
    "admin.libcms.sync.preview": "Preview (dry-run). Nothing has been changed yet:",
    "admin.libcms.sync.confirm": "Apply synchronization",
    "admin.libcms.sync.cancel": "Cancel",
    "admin.libcms.sync.done": "Synchronization complete:",
    "admin.libcms.sync.close": "Close",
    "admin.libcms.discipline": "Discipline",
    "admin.libcms.discipline.none": "Unassigned",
    "admin.libcms.discipline.logic": "Stoic Logic",
    "admin.libcms.discipline.physics": "Stoic Physics",
    "admin.libcms.discipline.ethics": "Stoic Ethics",
    "admin.libcms.preview": "Preview",
    "admin.libcms.publish": "Publish",
    "admin.libcms.unpublish": "Unpublish",
    "admin.libcms.toReview": "To review",
    "admin.libcms.history": "History",
    "admin.libcms.historyTitle": "Version history",
    "admin.libcms.noVersions": "No versions recorded yet.",
    "admin.libcms.publishEvent": "published",
    "admin.libcms.approvalTitle": "Founder Approval",
    "admin.libcms.approvalText": "You are about to publish this Journey to the Living Library:",
    "admin.libcms.approvalCheck": "I have reviewed the English and Dutch versions and approve publication.",
    "admin.libcms.confirmPublish": "Approve & publish",
    "admin.libcms.cancel": "Cancel",
    "admin.libcms.publishBlocked": "Publication blocked. Bilingual parity incomplete:",
    "admin.libcms.applySeed": "Apply content update",
    "admin.library.intro": "The Library is the reference layer of StoicOs.ai: books, quotes, sources, and eventually AI-indexed embeddings. UI shell prepared; backend endpoints will slot in without further layout work.",
    "admin.library.books": "Books",
    "admin.library.books.desc": "Physical & digital library. Cover, author, notes, tags.",
    "admin.library.readingLists": "Reading Lists",
    "admin.library.readingLists.desc": "Curated pathways (Beginner Stoic, Marcus deep dive, etc.).",
    "admin.library.quotes": "Quotes",
    "admin.library.quotes.desc": "Attributable quote bank tied to books and translations.",
    "admin.library.sources": "Historical Sources",
    "admin.library.sources.desc": "Primary sources: Meditations, Discourses, Letters.",
    "admin.library.pdfs": "Uploaded PDFs",
    "admin.library.pdfs.desc": "Reference PDFs for personal study, with a searchable text layer.",
    "admin.library.search": "Search",
    "admin.library.search.desc": "Full-text search across the whole library.",
    "admin.library.categories": "Categories",
    "admin.library.categories.desc": "Virtue · Emotion · Practice · Philosophy · Era.",
    "admin.library.collections": "Collections",
    "admin.library.collections.desc": "Handpicked bundles that surface on the public site.",
    "admin.library.aiIndex": "AI Indexing",
    "admin.library.aiIndex.desc": "Semantic embeddings for retrieval-augmented answers.",

    // ---------- Messages ----------
    "admin.messages.title": "Messages",
    "admin.messages.intro": "Every reflection sent from \u201cShare Your Thoughts\u201d on the public site lands here. Reply flow is on the roadmap.",
    "admin.messages.stats.total": "Total Messages",
    "admin.messages.stats.recent": "Last 7 Days",
    "admin.messages.stats.unread": "Unread",
    "admin.messages.stats.unread.hint": "Reply UI coming",
    "admin.messages.search": "Search name, email, body…",
    "admin.messages.name": "Name",
    "admin.messages.email": "Email",
    "admin.messages.subject": "Subject",
    "admin.messages.message": "Message",
    "admin.messages.received": "Received",
    "admin.messages.reply": "Reply",
    "admin.messages.from": "From",
    "admin.messages.selectPrompt": "Select a message to read it.",
    "admin.messages.noneMatch": "No messages match.",
    "admin.messages.anonymous": "(anonymous)",
    "admin.messages.noName": "(no name)",

    // ---------- Downloads ----------
    "admin.downloads.title": "Downloads",
    "admin.downloads.intro": "Distribution telemetry for the Virtue Guide iOS & Android apps. Numbers will populate once App Store Connect / Play Console reporting is wired.",
    "admin.downloads.total": "Total Installs",
    "admin.downloads.week": "Last 7 Days",
    "admin.downloads.ios": "iOS",
    "admin.downloads.android": "Android",
    "admin.downloads.appstoreLive": "Live · Compliance 3.1.1 passed.",
    "admin.downloads.playSoon": "Live · Official Google Play badge active on the public site.",
    "admin.downloads.version": "Version",
    "admin.downloads.lastRelease": "Last release",

    // ---------- Settings ----------
    "admin.settings.title": "Settings",
    "admin.settings.intro": "Configuration center. Secret values are never exposed. They display as •••••••• and can only be rotated in the production environment.",
    "admin.settings.general": "General",
    "admin.settings.general.sub": "Site identity & primary domain.",
    "admin.settings.publicDomain": "Public Domain",
    "admin.settings.apiBase": "API Base",
    "admin.settings.auth": "Authentication",
    "admin.settings.auth.sub": "Admin bearer token used for every /api/admin call.",
    "admin.settings.ai": "AI Providers",
    "admin.settings.ai.sub": "Model integrations. Keys held only server-side.",
    "admin.settings.apis": "API Keys",
    "admin.settings.apis.sub": "Third-party integrations.",
    "admin.settings.autoseo": "AutoSEO",
    "admin.settings.autoseo.sub": "Webhook-driven article ingest.",
    "admin.settings.env": "Environment",
    "admin.settings.env.sub": "Deployment & runtime state.",
    "admin.settings.flags": "Feature Flags",
    "admin.settings.flags.sub": "Toggle experimental surfaces (UI-only for now).",
    "admin.settings.journal": "Journal",
    "admin.settings.journal.sub": "Publication defaults.",
    "admin.settings.analyticsFlags": "Analytics",
    "admin.settings.analyticsFlags.sub": "Which surfaces emit events.",
    "admin.settings.downloadsFlags": "Downloads",
    "admin.settings.downloadsFlags.sub": "Virtue Guide app distribution.",
    "admin.settings.language": "Language",
    "admin.settings.theme": "Theme",
    "admin.settings.security": "Security",
    "admin.settings.website": "Website",
    "admin.settings.seo": "SEO",

    // ---------- System ----------
    "admin.system.title": "System",
    "admin.system.intro": "Real-time subsystem health. Refresh runs an authenticated ping against the admin API and reports each subsystem\u2019s status.",
    "admin.system.api": "API",
    "admin.system.db": "Database",
    "admin.system.storage": "Storage",
    "admin.system.webhook": "AutoSEO Webhook",
    "admin.system.uptime": "Session Uptime",
    "admin.system.env": "Environment",
    "admin.system.version": "Version",
    "admin.system.logs": "Logs",
    "admin.system.lastCheck": "Last Check",
    "admin.system.region": "Region",
    "admin.system.frontend": "Frontend",
    "admin.system.backend": "Backend",

    // ---------- Notifications ----------
    "admin.notif.title": "Notifications",
    "admin.notif.none": "No notifications yet.",
    "admin.notif.published": "Journal published",
    "admin.notif.updated": "Article updated",
    "admin.notif.autoseo": "SEO article waiting",
    "admin.notif.message": "New contact message",
    "admin.notif.system": "System notice",

    // ---------- Confirmations ----------
    "admin.confirm.delete": "Delete \u201c{title}\u201d permanently?",
    "admin.confirm.deleteShort": "Delete article?",
    "admin.inbox.title": "Content Inbox",
    "admin.inbox.subtitle": "Everything that requires your attention today.",
    "admin.inbox.refresh": "Refresh",
    "admin.inbox.counter.newToday": "New today",
    "admin.inbox.counter.drafts": "Drafts",
    "admin.inbox.counter.waitingNl": "Waiting for NL",
    "admin.inbox.counter.readyToPublish": "Ready to publish",
    "admin.inbox.counter.publishedToday": "Published today",
    "admin.inbox.source.autoseo": "AutoSEO",
    "admin.inbox.source.manual": "Manual",
    "admin.inbox.status.draft": "Draft",
    "admin.inbox.status.published": "Published",
    "admin.inbox.action.review": "Review",
    "admin.inbox.action.edit": "Edit",
    "admin.inbox.action.translate": "Translate to Dutch",
    "admin.inbox.action.publish": "Publish",
    "admin.inbox.action.unpublish": "Unpublish",
    "admin.inbox.action.delete": "Delete",
    "admin.inbox.action.more": "More actions",
    "admin.inbox.empty.title": "Everything is up to date.",
    "admin.inbox.empty.body": "The wise maintain their work before seeking more.",
    "admin.inbox.time.now": "just now",
    "admin.inbox.time.mins": "{n} min ago",
    "admin.inbox.time.hrs": "{n} h ago",
    "admin.inbox.time.days": "{n} d ago",
    "admin.lexicon.eyebrow": "Ecosystem",
    "admin.lexicon.title": "StoicOs Lexicon",
    "admin.lexicon.subtitle": "The single source of truth for every AI workflow: translation, AutoSEO, Holo AI campaigns, agents, SEO, social. Protected terms are never translated or altered in any language.",
    "admin.lexicon.stat.total": "Total terms",
    "admin.lexicon.stat.enabled": "Active",
    "admin.lexicon.stat.brand": "Brand",
    "admin.lexicon.stat.philosophy": "Philosophy",
    "admin.lexicon.tab.all": "All",
    "admin.lexicon.cat.brand": "Brand",
    "admin.lexicon.cat.philosophy": "Philosophy",
    "admin.lexicon.cat.historical": "Historical",
    "admin.lexicon.searchPlaceholder": "Search terms, aliases, notes…",
    "admin.lexicon.empty": "No terms match your filter.",
    "admin.lexicon.disabled": "Disabled",
    "admin.lexicon.aliases": "Aliases",
    "admin.lexicon.action.new": "New term",
    "admin.lexicon.action.import": "Import JSON",
    "admin.lexicon.action.export": "Export JSON",
    "admin.lexicon.action.enable": "Enable protection",
    "admin.lexicon.action.disable": "Disable protection",
    "admin.lexicon.field.term": "Term (canonical form)",
    "admin.lexicon.field.category": "Category",
    "admin.lexicon.field.aliases": "Aliases",
    "admin.lexicon.field.aliasesHint": "Alternate spellings that should also be protected. Separate with commas.",
    "admin.lexicon.field.notes": "Notes (internal only)",
    "admin.lexicon.field.enabled": "Protection enabled",
    "admin.lexicon.editor.new": "New Lexicon term",
    "admin.lexicon.editor.edit": "Edit Lexicon term",
    "admin.lexicon.err.termRequired": "Term is required.",
    "admin.lexicon.msg.saved": "Term saved. Protection is active on the next AI call.",
    "admin.lexicon.msg.created": "Term added to the Lexicon.",
    "admin.lexicon.msg.deleted": "Removed \u201c{term}\u201d.",
    "admin.lexicon.msg.exported": "Exported {n} terms as JSON.",
    "admin.lexicon.msg.imported": "Imported: {inserted} added, {skipped} duplicates, {invalid} rejected.",
    "admin.lexicon.confirm.delete": "Remove \u201c{term}\u201d from the Lexicon? It will no longer be protected in AI-generated content.",
    "admin.lexicon.help": "Every AI translation, AutoSEO article, future Holo AI campaign and Founder Portal agent consumes this Lexicon before generating text. Edits take effect immediately (backend cache is invalidated on every save).",
    "admin.vault.eyebrow": "Foundation",
    "admin.vault.title": "Content Vault",
    "admin.vault.tagline": "Create Once. Store Forever. Reuse Everywhere.",
    "admin.vault.stat.total": "Assets stored",
    "admin.vault.upload.title": "Drop images here",
    "admin.vault.upload.subtitle": "Or select multiple files. Every asset receives a permanent Content ID (SW-000001).",
    "admin.vault.upload.formats": "JPG · PNG · WEBP · PDF · up to {max} MB each",
    "admin.vault.upload.button": "Select files",
    "admin.vault.tab.all": "All",
    "admin.vault.tab.draft": "Draft",
    "admin.vault.tab.published": "Published",
    "admin.vault.tab.archived": "Archived",
    "admin.vault.searchPlaceholder": "Search by title, Content ID, or original filename\u2026",
    "admin.vault.loadMore": "Load more",
    "admin.vault.empty.title": "Nothing here yet.",
    "admin.vault.empty.body": "Every original piece of knowledge: created once, stored forever, reused everywhere.",
    "admin.vault.field.title": "Title",
    "admin.vault.field.author": "Author",
    "admin.vault.field.uploaded": "Uploaded",
    "admin.vault.field.filename": "Original filename",
    "admin.vault.field.type": "File type",
    "admin.vault.field.size": "Size",
    "admin.vault.field.dims": "Dimensions",
    "admin.vault.field.status": "Status",
    "admin.vault.action.viewOriginal": "View original",
    "admin.vault.confirm.delete": "Delete \u201c{title}\u201d? This soft-deletes the asset (original bytes are preserved in storage).",
    "admin.vault.readiness.ready":               "Ready for Site Media",
    "admin.vault.readiness.no_public_url":       "Missing public URL",
    "admin.vault.readiness.upload_incomplete":   "Upload incomplete",
    "admin.vault.readiness.storage_unreachable": "Storage unreachable",
    "admin.vault.readiness.generic":             "Not ready",
    "admin.vault.readiness.sectionTitle":        "Publish readiness",
    "admin.vault.readiness.check.has_content_id":    "Permanent Content ID",
    "admin.vault.readiness.check.has_storage_path":  "Storage path set",
    "admin.vault.readiness.check.has_file_size":     "File size recorded",
    "admin.vault.readiness.check.upload_complete":   "Upload finished",
    "admin.vault.readiness.check.storage_reachable": "Bytes reachable",
    "admin.vault.readiness.verify":              "Verify publish readiness",
    "admin.vault.readiness.verifying":           "Verifying\u2026",
    "admin.vault.readiness.publicUrl":           "Public URL",
    "admin.vault.readiness.publishBlocked":      "Cannot publish: {reason}",
    "admin.vault.ai.title": "AI Enrichment",
    "admin.vault.ai.intro": "Analyse this asset with Claude Vision. Extract OCR, tags, virtues, and bilingual metadata. Nothing is saved until you confirm.",
    "admin.vault.ai.analyze": "Analyse with AI",
    "admin.vault.ai.reanalyze": "Re-analyse",
    "admin.vault.ai.confirmed": "AI confirmed",
    "admin.vault.ai.working": "Analysing…",
    "admin.vault.ai.workingHint": "OCR + metadata extraction. This can take 20\u201360 seconds for PDFs.",
    "admin.vault.ai.proposal.title": "AI Proposal",
    "admin.vault.ai.proposal.hint": "Edit anything you disagree with. Nothing is saved until you press Confirm.",
    "admin.vault.ai.proposal.brandProtected": "Brand terms shielded via Lexicon",
    "admin.vault.ai.proposal.promoteTitle": "Also replace the asset\u2019s primary title with the English AI title",
    "admin.vault.ai.proposal.confirm": "Confirm & Save",
    "admin.vault.ai.proposal.discard": "Discard",
    "admin.vault.ai.proposal.page": "page",
    "admin.vault.ai.proposal.pages": "pages",
    "admin.vault.ai.field.titleEn": "Title (EN)",
    "admin.vault.ai.field.titleNl": "Title (NL)",
    "admin.vault.ai.field.descriptionEn": "Description (EN)",
    "admin.vault.ai.field.descriptionNl": "Description (NL)",
    "admin.vault.ai.field.detectedLanguage": "Detected language",
    "admin.vault.ai.field.tags": "Tags",
    "admin.vault.ai.field.themes": "Themes",
    "admin.vault.ai.field.virtues": "Stoic virtues",
    "admin.vault.ai.field.emotions": "Emotions",
    "admin.vault.ai.field.ocr": "OCR transcript",
    "admin.vault.bulk.button": "Analyse all unprocessed",
    "admin.vault.bulk.buttonHint": "Batch-analyse every asset that does not yet have a confirmed AI enrichment.",
    "admin.vault.bulk.confirmTitle": "Batch AI Enrichment",
    "admin.vault.bulk.confirmBody": "About to analyse {n} unprocessed asset(s) with Claude Vision. Each one takes 5\u201330 s. Proposals will be auto-saved as \"confirmed (bulk)\" so downstream reuse works. You can still edit or re-analyse any asset afterwards.",
    "admin.vault.bulk.confirmWarning": "Unlike single-asset analysis, bulk mode does NOT stop for manual review. If you need to review each proposal, close this dialog and analyse assets one-by-one instead.",
    "admin.vault.bulk.start": "Start batch",
    "admin.vault.bulk.progressTitle": "Batch AI Enrichment · Progress",
    "admin.vault.bulk.processed": "processed",
    "admin.vault.bulk.done": "Done",
    "admin.vault.bulk.failed": "Failed",
    "admin.vault.bulk.skipped": "Skipped",
    "admin.vault.bulk.log": "Live log",
    "admin.vault.bulk.logEmpty": "Waiting for the worker\u2026",
    "admin.vault.bulk.cancelJob": "Cancel job",
    "admin.vault.bulk.closeDisabled": "Job is still running. Cancel it first if you want to stop.",
    "admin.vault.action.replace": "Replace bytes",
    "admin.vault.replace.confirm": "Replace this asset's bytes with the new file? Content ID stays the same, all consumers will pick up the new file automatically. This cannot be undone.",
    "admin.vault.usedIn.title": "Used In",
    "admin.vault.usedIn.loading": "Checking usage\u2026",
    "admin.vault.usedIn.empty": "Not yet used anywhere.",
    "admin.vault.usedIn.summary": "Referenced in {n} place(s).",
    "admin.vault.integrity.check": "Verify integrity",
    "admin.vault.integrity.ok": "Bytes intact",
    "admin.vault.integrity.fail": "Bytes mismatch",
    "admin.confirm.publish": "Publish article?",
    "admin.confirm.save": "Save changes?",
    "admin.confirm.cancelEdit": "Cancel editing?",

    // ---------- Publishing Studio ----------
    "pubstudio.title": "Publishing Studio",
    "pubstudio.tab.journal":    "Journal",
    "pubstudio.tab.social":     "Social",
    "pubstudio.tab.newsletter": "Newsletter",
    "pubstudio.tab.analytics":  "Analytics",

    "pubstudio.intro.journal":    "Every published article, ready to be turned into distribution copy. Open one to begin.",
    "pubstudio.intro.social":     "Craft platform-specific copy that always brings readers back to stoicos.ai.",
    "pubstudio.intro.newsletter": "Compose a weekly digest from your published articles.",
    "pubstudio.intro.analytics":  "Live counters from your publishing activity. Traffic and reader metrics arrive with the analytics integration.",

    "pubstudio.loading":      "Loading…",
    "pubstudio.back":         "Back to Journal",
    "pubstudio.generate":     "Generate",
    "pubstudio.regenerate":   "Regenerate",
    "pubstudio.generating":   "Generating…",

    "pubstudio.empty.title": "No published articles yet.",
    "pubstudio.empty.body":  "Publish an article in the Journal Command Center and it will appear here.",

    "pubstudio.card.open":     "Open",
    "pubstudio.card.generate": "Generate",
    "pubstudio.card.min":      "min",
    "pubstudio.card.posts":    "posts",

    "pubstudio.social.forArticle":     "For article",
    "pubstudio.linkedin.imageChecking": "Checking LinkedIn preview image…",
    "pubstudio.linkedin.imageReady":    "LinkedIn preview image ready",
    "pubstudio.linkedin.noImage":       "No LinkedIn image attached.",
    "pubstudio.social.preview":        "Preview",
    "pubstudio.social.notGenerated":   "Not generated yet",
    "pubstudio.social.lastGenerated":  "Last generated",
    "pubstudio.social.emptyBody":      "Nothing has been generated for this platform yet.",
    "pubstudio.social.emptyHint":      "Press Generate to draft a post in Ed's voice.",
    "pubstudio.social.pickTitle":      "Pick an article to begin.",
    "pubstudio.social.pickBody":       "Return to the Journal tab and choose an article, or select one below.",

    "pubstudio.newsletter.eyebrow":   "Weekly Digest",
    "pubstudio.newsletter.title":     "Compose this week's newsletter.",
    "pubstudio.newsletter.body":      "Select the articles you want to feature. The digest will be written as a personal letter from the founder, ready to paste into Substack or any email tool.",
    "pubstudio.newsletter.pick":      "Included articles",
    "pubstudio.newsletter.selected":  "selected",
    "pubstudio.newsletter.generate":  "Generate Digest",
    "pubstudio.newsletter.digest":    "Weekly Digest",
    "pubstudio.newsletter.field.subject": "Subject",
    "pubstudio.newsletter.field.intro":   "Introduction",
    "pubstudio.newsletter.field.main":    "Main Article",
    "pubstudio.newsletter.field.closing": "Closing Reflection",
    "pubstudio.newsletter.field.cta":     "Call to Action",

    "pubstudio.analytics.articlesPublished":  "Articles Published",
    "pubstudio.analytics.linkedinPosts":      "LinkedIn Posts",
    "pubstudio.analytics.substackPosts":      "Substack Posts",
    "pubstudio.analytics.totalGenerated":     "Total Generated",
    "pubstudio.analytics.byPlatform":         "Generated Per Platform",
    "pubstudio.analytics.mostGenerated":      "Most Distributed Articles",
    "pubstudio.analytics.posts":              "posts",
    "pubstudio.analytics.noData":             "No distribution posts generated yet.",
    "pubstudio.analytics.trafficSources":     "Traffic Sources",
    "pubstudio.analytics.readingTime":        "Average Reading Time",
    "pubstudio.analytics.ctrVirtueGuide":     "CTR to Virtue Guide",
    "pubstudio.analytics.pendingIntegration": "Available after GA / Clarity integration",
  },

  nl: {
    // ---------- Common / actions ----------
    "admin.action.edit": "Bewerken",
    "admin.action.delete": "Verwijderen",
    "admin.action.publish": "Publiceren",
    "admin.action.unpublish": "Depubliceren",
    "admin.action.preview": "Voorbeeld",
    "admin.action.save": "Opslaan",
    "admin.action.cancel": "Annuleren",
    "admin.action.saveDraft": "Concept opslaan",
    "admin.action.update": "Artikel bijwerken",
    "admin.action.create": "Artikel aanmaken",
    "admin.action.new": "Nieuw",
    "admin.action.search": "Zoeken",
    "admin.action.filter": "Filter",
    "admin.action.open": "Openen",
    "admin.action.close": "Sluiten",
    "admin.action.refresh": "Vernieuwen",
    "admin.action.copy": "Kopiëren",
    "admin.action.reveal": "Tonen",
    "admin.action.hide": "Verbergen",
    "admin.action.testConnection": "Verbinding testen",
    "admin.action.markAllRead": "Alles als gelezen markeren",
    "admin.action.viewAll": "Alles bekijken",
    "admin.action.viewAllArticles": "Alle artikelen bekijken",
    "admin.action.retest": "Statuscontroles opnieuw uitvoeren",
    "admin.action.enter": "Command Center openen",

    // ---------- Common labels / status ----------
    "admin.label.loading": "Laden…",
    "admin.label.saving": "Opslaan…",
    "admin.label.verifying": "Verifiëren…",
    "admin.label.total": "Totaal",
    "admin.label.recent": "Recent",
    "admin.label.recentActivity": "Recente activiteit",
    "admin.label.recentArticles": "Recente artikelen",
    "admin.label.published": "Gepubliceerd",
    "admin.label.draft": "Concept",
    "admin.label.drafts": "Concepten",
    "admin.label.status": "Status",
    "admin.label.online": "Online",
    "admin.label.offline": "Offline",
    "admin.label.connected": "Verbonden",
    "admin.label.disconnected": "Niet verbonden",
    "admin.label.configured": "Geconfigureerd",
    "admin.label.notSet": "Niet ingesteld",
    "admin.label.nominal": "Normaal",
    "admin.label.on": "Aan",
    "admin.label.off": "Uit",
    "admin.label.ok": "OK",
    "admin.label.down": "Offline",
    "admin.label.inDevelopment": "In ontwikkeling",
    "admin.label.comingSoon": "Binnenkort",
    "admin.label.placeholder": "Voorbeeldwaarde",
    "admin.label.private": "Privé",
    "admin.label.founderOnlyAccess": "Alleen voor de oprichter · Geen openbare registratie",
    "admin.label.authorised": "Uitsluitend voor bevoegd personeel",
    "admin.label.live": "Live",
    "admin.label.lifetime": "totaal",
    "admin.label.rollingWeek": "Voortschrijdende week",
    "admin.label.last7d": "Afgelopen 7 dagen",
    "admin.journal.section.published": "Gepubliceerd",
    "admin.journal.section.drafts": "Concepten",
    "admin.label.today": "Vandaag",
    "admin.label.yesterday": "Gisteren",
    "admin.label.language": "Taal",

    // ---------- Sidebar / navigation ----------
    "admin.nav.dashboard": "Dashboard",
    "admin.nav.publishingStudio": "Publishing Studio",
    "admin.nav.journal": "Journaal",
    "admin.nav.editorial": "Editorial Dashboard",
    "admin.nav.contentCalendar": "Contentkalender",
    "admin.nav.newsletter": "Nieuwsbrief",
    "admin.nav.seoCenter": "SEO Center",
    "admin.nav.aiJobs": "AI Job Monitor",
    "admin.nav.group.content": "Content",
    "admin.nav.group.academy": "Academy",
    "admin.nav.group.media": "Media",
    "admin.nav.group.marketing": "Marketing",
    "admin.nav.group.system": "Systeem",
    "admin.nav.lexicon": "Lexicon",
    "admin.nav.vault": "Kluis",
    "admin.nav.siteMedia": "Site Media",
    "admin.nav.students": "Studenten",
    "admin.nav.dailyInspiration": "Daily Inspiration",
    "admin.nav.founders": "Founders Wall",
    "admin.founders.title": "Founder Portal",
    "admin.founders.subtitle": "Founding Members worden UITSLUITEND toegevoegd via expliciete bevestiging hieronder — nooit automatisch via registratie, testaccounts of aanwezigheid in de database. Volledige gegevens zijn alleen hier zichtbaar.",
    "admin.founders.confirmTitle": "Founding Member bevestigen",
    "admin.founders.confirmHint": "Voer het account-e-mailadres in van de persoon die u expliciet als Founding Member bevestigt. Dit is de enige weg naar de Wall.",
    "admin.founders.confirmPlaceholder": "lid@email.com",
    "admin.founders.confirmBtn": "Bevestigen",
    "admin.founders.confirmed": "Bevestigd",
    "admin.founders.alreadyConfirmed": "Deze persoon is al een bevestigd Founding Member.",
    "admin.founders.revoke": "Intrekken",
    "admin.founders.revokeConfirm": "Deze persoon volledig van de Founders Wall verwijderen? De nummering sluit automatisch aan zonder gaten.",
    "admin.founders.editName": "Publieke Wall-naam bewerken (wijzigt niet het accountprofiel)",
    "admin.founders.editNamePrompt": "Publieke weergavenaam (formaat: voornaam + eerste letter achternaam, bijv. Daniel M.). Leeg laten om af te leiden van de accountnaam.",
    "admin.founders.count": "Founding Members",
    "admin.founders.remaining": "Resterende plaatsen",
    "admin.founders.onWall": "Zichtbaar op de Wall",
    "admin.founders.pending": "Wacht op keuze",
    "admin.founders.colNumber": "Nr.",
    "admin.founders.colPublic": "Publieke naam op de Wall",
    "admin.founders.publicNamePlaceholder": "bijv. Saskia L.",
    "admin.founders.publicNameHint": "Wijzigt uitsluitend hoe de naam op de publieke Founders Wall verschijnt (formaat: voornaam + eerste letter achternaam). Accountnaam, e-mail en profiel blijven onaangetast. Het lidnummer wijzigt nooit.",
    "admin.founders.colName": "Volledige naam",
    "admin.founders.colEmail": "E-mail",
    "admin.founders.colDate": "Geregistreerd",
    "admin.founders.colPref": "Voorkeur",
    "admin.founders.colActions": "Acties",
    "admin.founders.show": "Toon op wall",
    "admin.founders.hide": "Verberg van wall",
    "admin.founders.remove": "Verwijderen",
    "admin.founders.restore": "Herstellen",
    "admin.founders.removed": "Verwijderd",
    "admin.founders.prefYes": "Ja",
    "admin.founders.prefNo": "Nee",
    "admin.founders.prefPending": "In afwachting",
    "admin.students.title": "Studenten",
    "admin.students.subtitle": "Beheer studentaccounts en onboardingstatus.",
    "admin.students.search": "Zoek op naam of e-mail…",
    "admin.students.done": "Voltooid",
    "admin.students.pending": "Niet voltooid",
    "admin.students.reset": "Onboarding resetten",
    "admin.students.resetConfirm": "Onboarding resetten voor {name}? De volledige onboarding wordt bij de volgende login opnieuw getoond.",
    "admin.students.empty": "Geen studenten gevonden.",
    "admin.nav.logicMedia": "Logica Media",
    "admin.nav.logicCms": "Logica CMS",
    "admin.nav.physicsMedia": "Fysica Media",
    "admin.nav.physicsCms": "Fysica CMS",
    "admin.nav.ethicsMedia": "Ethica Media",
    "admin.nav.ethicsCms": "Ethica CMS",
    "admin.nav.library": "Bibliotheek",
    "admin.nav.autoseo": "AutoSEO",
    "admin.nav.analytics": "Analyse",
    "admin.nav.downloads": "Downloads",
    "admin.nav.messages": "Berichten",
    "admin.nav.settings": "Instellingen",
    "admin.nav.system": "Systeem",
    "admin.nav.logout": "Uitloggen",
    "admin.nav.backHome": "StoicOs.ai Home",
    "admin.brand": "StoicOs.ai · Command Center",

    // ---------- Login ----------
    "admin.login.brand": "StoicOs.ai",
    "admin.login.title": "Founder Portal",
    "admin.login.subtitle": "Privétoegang tot het StoicOs.ai Command Center.",
    "admin.login.tokenLabel": "Toegangstoken",
    "admin.login.tokenPlaceholder": "Voer uw token in",
    "admin.login.invalid": "Ongeldig token.",
    "admin.login.networkError": "Netwerkfout.",
    "admin.login.serverError": "Serverfout ({status}). Probeer het opnieuw.",

    // ---------- Dashboard ----------
    "admin.dash.welcome": "Welkom terug, Ed.",
    "admin.dash.welcomeSub": "Uw privé StoicOs.ai Command Center.",
    "admin.dash.quickActions": "Snelle acties",
    "admin.dash.newArticle": "Nieuw journaalartikel",
    "admin.dash.openJournal": "Journaal openen",
    "admin.dash.openEditorial": "Editorial Dashboard",
    "admin.dash.openDashboard": "Dashboard openen",
    "admin.dash.daily": "Dagelijks Command Center",
    "admin.dash.stat.drafts": "Concepten",
    "admin.dash.stat.drafts.hint": "Wachten op beoordeling",
    "admin.dash.stat.published": "Gepubliceerd",
    "admin.dash.stat.published.hint": "Live op de site",
    "admin.dash.stat.total": "Totaal artikelen",
    "admin.dash.stat.total.hint": "Journaalbibliotheek",
    "admin.dash.stat.messages": "Nieuwe berichten",
    "admin.dash.stat.visitors": "Bezoekers vandaag",
    "admin.dash.stat.visitors.hint": "GA4 koppelen",
    "admin.dash.stat.downloads": "VG-downloads",
    "admin.dash.stat.autoseo": "AutoSEO",
    "admin.dash.stat.autoseo.noImports": "Nog geen imports",
    "admin.dash.stat.autoseo.last": "Laatste: {date}",
    "admin.dash.stat.system": "Systeem",
    "admin.dash.stat.system.hint": "{count} op wachtlijst",
    "admin.dash.noArticles": "Nog geen artikelen. Maak er een aan vanuit het Journaal.",

    // ---------- Journal admin ----------
    "admin.journal.title": "Journaalbeheer",
    "admin.journal.backToDash": "Terug naar dashboard",
    "admin.journal.editor.new": "Nieuw artikel",
    "admin.journal.editor.edit": "Artikel bewerken",
    "admin.journal.editor.title": "Titel",
    "admin.journal.editor.excerpt": "Samenvatting",
    "admin.journal.editor.slug": "Slug",
    "admin.journal.editor.body": "Tekst",
    "admin.journal.editor.tags": "Tags",
    "admin.journal.editor.category": "Categorie",
    "admin.journal.editor.cover": "Uitgelichte afbeelding",
    "admin.journal.editor.pickVault": "Kies uit Content Vault",
    "admin.journal.editor.seoTitle": "SEO-titel",
    "admin.journal.editor.seoDescription": "SEO-omschrijving",
    "admin.journal.editor.featured": "Uitgelicht",
    "admin.journal.editor.author": "Auteur",
    "admin.journal.msg.editing": "Bezig met bewerken: {title}",
    "admin.journal.localization.title":     "Lokalisatie-gezondheid",
    "admin.journal.localization.loading":   "Analyseren van gepubliceerde artikelen\u2026",
    "admin.journal.localization.healthy":   "Alle {total} gepubliceerde artikelen zijn volledig tweetalig.",
    "admin.journal.localization.summary":   "{missing} van {total} gepubliceerde artikelen missen Nederlandse velden en vallen op stoicos.ai/nl terug naar Engels.",
    "admin.journal.localization.fixAll":    "Vertaal ontbrekende NL",
    "admin.journal.localization.refresh":   "Ververs",
    "admin.journal.localization.expand":    "Details",
    "admin.journal.localization.collapse":  "Verbergen",
    "admin.journal.localization.col.article": "Artikel",
    "admin.journal.localization.col.reason":  "Oorzaak",
    "admin.journal.localization.col.fix":     "Herstel",
    "admin.journal.localization.col.retranslate": "Vertaal opnieuw",
    "admin.journal.list.drafts": "Concepten",
    "admin.journal.list.published": "Gepubliceerd",
    "admin.journal.list.empty": "Nog geen artikelen.",
    "admin.journal.trans.both": "EN + NL",
    "admin.journal.trans.en_only": "Alleen EN",
    "admin.journal.trans.nl_only": "Alleen NL",
    "admin.journal.trans.pending_nl": "NL loopt",
    "admin.journal.trans.failed_nl": "NL mislukt",
    "admin.journal.trans.none": "geen titel",
    "admin.journal.trans.tooltip": "Tweetalige vertaalstatus. Zowel EN als NL moeten ingevuld zijn voordat Nederlandse bezoekers dit artikel in hun eigen taal kunnen lezen.",
    "admin.journal.trans.filter.all": "Alle artikelen",
    "admin.journal.trans.filter.missing_nl": "NL ontbreekt",
    "admin.journal.trans.filter.missing_en": "EN ontbreekt",
    "admin.journal.trans.filter.label": "Vertaling",
    "admin.journal.trans.warn.title": "Nederlandse vertaling ontbreekt",
    "admin.journal.trans.warn.body": "Dit artikel wordt alleen in het Engels gepubliceerd. Nederlandse bezoekers zien stilzwijgend de Engelse versie totdat u de NL-velden hieronder invult.",
    "admin.journal.trans.warn.title_en": "Engelse vertaling ontbreekt",
    "admin.journal.trans.warn.body_en": "Dit artikel heeft geen Engelse inhoud. Vul de EN-velden hierboven aan, anders zien Engelstalige bezoekers de Nederlandse tekst als terugvaloptie.",
    "admin.journal.trans.copy": "Kopieer van EN",
    "admin.journal.trans.copied": "EN → NL gekopieerd",
    "admin.journal.trans.ai.button": "Vertaal met AI",
    "admin.journal.trans.ai.working": "Bezig met vertalen door Claude. Lange artikelen kunnen enkele minuten duren — houd dit venster open…",
    "admin.journal.trans.ai.working_short": "Vertalen…",
    "admin.journal.trans.ai.done": "Nederlandse concepttekst klaar. Controleer hieronder en sla op.",
    "admin.journal.trans.ai.needSave": "Sla het artikel eerst op, klik daarna op Vertalen.",
    "admin.journal.trans.ai.failed": "De vertaling is mislukt. Er is niets aan het artikel gewijzigd. Probeer het opnieuw.",
    "admin.journal.trans.ai.stillRunning": "De vertaling draait nog op de achtergrond. Er is nog niets gewijzigd — probeer het over een minuut opnieuw.",
    "admin.journal.approval.title": "Founder-goedkeuring",
    "admin.journal.approval.en": "Engelse versie goedkeuren",
    "admin.journal.approval.nl": "Nederlandse versie goedkeuren",
    "admin.journal.approval.required": "Goedkeuring vereist",
    "admin.journal.approval.ready": "Klaar om te publiceren",
    "admin.journal.approval.saveFirst": "Sla het artikel eerst op en keur daarna beide taalversies goed.",
    "admin.journal.approval.blocked": "Dit artikel kan nog niet gepubliceerd worden: keur eerst de Engelse én Nederlandse versie goed in het Founder-goedkeuring gedeelte.",
    "admin.journal.cleanup.title": "AutoSEO-publicatiecontrole",
    "admin.journal.cleanup.run": "Opschoonprotocol uitvoeren",
    "admin.journal.cleanup.notRun": "Het verplichte opschoonprotocol is nog niet uitgevoerd op dit artikel. Voer het uit om externe links, verwijsteksten, AI-restanten en placeholders te verwijderen.",
    "admin.journal.cleanup.passed": "Geslaagd",
    "admin.journal.cleanup.failed": "Mislukt",
    "admin.journal.cleanup.yes": "ja",
    "admin.journal.cleanup.no": "nee",
    "admin.journal.cleanup.removed": "verwijderd",
    "admin.journal.cleanup.extLinks": "Externe links verwijderd",
    "admin.journal.cleanup.refText": "Bijbehorende verwijstekst verwijderd",
    "admin.journal.cleanup.aiRemnants": "AI-restanten verwijderd",
    "admin.journal.cleanup.placeholders": "Placeholders verwijderd",
    "admin.journal.cleanup.internal": "Interne StoicOs-links gecontroleerd",
    "admin.journal.cleanup.nlClean": "Nederlandse versie schoon",
    "admin.journal.cleanup.enClean": "Engelse versie schoon",
    "admin.journal.cleanup.failures": "Aangetroffen passages (moeten worden opgelost)",
    "admin.journal.cleanup.reviewFlags": "Inhoudelijke waarschuwingen — expliciet bevestigen vóór publicatie",
    "admin.journal.cleanup.blocked": "Publiceren is vergrendeld totdat het opschoonprotocol voor iedere taal is geslaagd.",
    "admin.journal.cleanup.msgPassed": "Opschoonprotocol geslaagd — het artikel is schoon.",
    "admin.journal.cleanup.msgFailed": "Opschoning uitgevoerd, maar er resteren problemen. Bekijk de passages hieronder.",
    "admin.journal.warnings.title": "Redactionele waarschuwingen",
    "admin.journal.warnings.intro": "De volgende passages zijn gemarkeerd voor redactionele controle (promotionele termen, naamsvermeldingen of onbevestigde actuele claims). Er is niets automatisch verwijderd — beoordeel iedere passage en bevestig expliciet vóór publicatie.",
    "admin.journal.warnings.open": "Wacht op bevestiging",
    "admin.journal.warnings.confirmed": "Bevestigd",
    "admin.journal.warnings.pending": "Waarschuwingen eerst bevestigen",
    "admin.journal.warnings.ackLabel": "Ik heb deze redactionele waarschuwingen beoordeeld en bevestig uitdrukkelijk dat dit artikel gepubliceerd mag worden.",
    "admin.journal.warnings.alreadyAck": "Eerder door de founder bevestigd.",
    "admin.journal.warnings.blocked": "Publiceren is vergrendeld totdat de redactionele waarschuwingen expliciet zijn bevestigd.",
    "admin.journal.warnings.promo": "Promotionele term",
    "admin.journal.warnings.named": "Naamsvermelding (controleren)",
    "admin.journal.warnings.claim": "Onbevestigde actuele claim",
    "admin.journal.bulk.title": "{count} artikel(en) zonder Nederlandse vertaling",
    "admin.journal.bulk.body": "Vertaal elk artikel waarvan de titel, samenvatting, inhoud of SEO-beschrijving nog ontbreekt in het Nederlands. Elke vertaling wordt opgeslagen als concept. U controleert vóór publicatie.",
    "admin.journal.bulk.button": "Bulkvertaling ontbrekend NL",
    "admin.journal.bulk.modal.eyebrow": "AI Bulkvertaling",
    "admin.journal.bulk.modal.title": "{count} artikel(en) naar het Nederlands vertalen",
    "admin.journal.bulk.modal.start": "Vertaling starten",
    "admin.journal.bulk.modal.retry": "Opnieuw proberen",
    "admin.journal.bulk.modal.cancel": "Annuleren",
    "admin.journal.bulk.modal.close": "Sluiten",
    "admin.journal.bulk.modal.progress": "{done} van {total} klaar",
    "admin.journal.bulk.copyError": "Kopieer",
    "admin.journal.bulk.errorKind": "{kind}",

    // ---------- AutoSEO ----------
    "admin.autoseo.title": "AutoSEO",
    "admin.autoseo.intro": "De AutoSEO-webhook ontvangt gegenereerde artikelen uit uw pipeline en plaatst ze als concept in het Journaal. Er wordt niets automatisch gepubliceerd.",
    "admin.autoseo.webhookStatus": "Webhookstatus",
    "admin.autoseo.bearerStatus": "Bearer-token",
    "admin.autoseo.articlesWaiting": "Wachtende artikelen",
    "admin.autoseo.totalImported": "Totaal geïmporteerd",
    "admin.autoseo.connection": "Verbinding",
    "admin.autoseo.endpoint": "Webhook-endpoint",
    "admin.autoseo.lastImport": "Laatst geïmporteerde artikel",
    "admin.autoseo.noImports": "Nog geen imports.",
    "admin.autoseo.openLogs": "AutoSEO-logboek openen",
    "admin.autoseo.history": "Importgeschiedenis",
    "admin.autoseo.historyEmpty": "Nog geen AutoSEO-imports.",
    "admin.autoseo.scheduled": "Geplande imports",
    "admin.autoseo.scheduledHint": "Terugkerende en geplande imports verschijnen hier zodra de AutoSEO-pipeline cron-gebaseerd publiceren ondersteunt.",
    "admin.autoseo.accepting": "Accepteert POST",
    "admin.autoseo.tokenMissing": "Token niet ingesteld",

    // ---------- Analytics ----------
    "admin.analytics.title": "Analyse",
    "admin.analytics.intro": "Live data wordt gekoppeld zodra elke integratie is aangesloten. De verkeersverdeling is een tijdelijke waarde totdat GA4-metrics via de server worden doorgestuurd.",
    "admin.analytics.visitors": "Websitebezoekers",
    "admin.analytics.journalViews": "Journaalweergaven",
    "admin.analytics.downloads": "Downloads",
    "admin.analytics.conversion": "Conversiepercentage",
    "admin.analytics.conversion.hint": "Wachtlijst → download",
    "admin.analytics.articlesLive": "Artikelen live",
    "admin.analytics.readingTime": "Gem. leestijd",
    "admin.analytics.returnVisitors": "Terugkerende bezoekers",
    "admin.analytics.waitlist": "Wachtlijst",
    "admin.analytics.waitlist.hint": "Totaal aantal aanmeldingen",
    "admin.analytics.traffic": "Verkeersbronnen",
    "admin.analytics.trafficNote": "Voorbeeldverdeling · Koppel GA4 voor live data",
    "admin.analytics.topArticles": "Meest bekeken artikelen",

    // ---------- Library ----------
    "admin.library.title": "Bibliotheek",
    "admin.libcms.title": "Living Library CMS",
    "admin.libcms.intro": "Founder-publicatieworkflow voor de Wisdom Journeys. Concept → In review → Gepubliceerd. Niets gaat live zonder uw goedkeuring. Publiceren activeert automatisch het Compass Rose-icoon.",
    "admin.libcms.empty": "Nog geen Journeys.",
    "admin.libcms.status.draft": "Concept",
    "admin.libcms.status.review": "In review",
    "admin.libcms.status.published": "Gepubliceerd",
    "admin.libcms.status.archived": "Gearchiveerd",
    "admin.libcms.hero": "Hero",
    "admin.libcms.lexicon": "Lexicon",
    "admin.libcms.reflection": "Reflectie",
    "admin.libcms.practice": "Praktijk",
    "admin.libcms.publishedAt": "Gepubliceerd",
    "admin.libcms.modified": "Laatst gewijzigd",
    "admin.libcms.versions": "Versies",
    "admin.libcms.sync": "Synchroniseer Journey-content",
    "admin.libcms.sync.preview": "Voorbeeld (dry-run). Er is nog niets gewijzigd:",
    "admin.libcms.sync.confirm": "Synchronisatie toepassen",
    "admin.libcms.sync.cancel": "Annuleren",
    "admin.libcms.sync.done": "Synchronisatie voltooid:",
    "admin.libcms.sync.close": "Sluiten",
    "admin.libcms.discipline": "Discipline",
    "admin.libcms.discipline.none": "Niet toegewezen",
    "admin.libcms.discipline.logic": "Stoïsche Logica",
    "admin.libcms.discipline.physics": "Stoïsche Fysica",
    "admin.libcms.discipline.ethics": "Stoïsche Ethiek",
    "admin.libcms.preview": "Voorbeeld",
    "admin.libcms.publish": "Publiceren",
    "admin.libcms.unpublish": "Depubliceren",
    "admin.libcms.toReview": "Naar review",
    "admin.libcms.history": "Historie",
    "admin.libcms.historyTitle": "Versiegeschiedenis",
    "admin.libcms.noVersions": "Nog geen versies vastgelegd.",
    "admin.libcms.publishEvent": "gepubliceerd",
    "admin.libcms.approvalTitle": "Founder-goedkeuring",
    "admin.libcms.approvalText": "U staat op het punt deze Journey te publiceren in de Living Library:",
    "admin.libcms.approvalCheck": "Ik heb de Engelse en Nederlandse versie gecontroleerd en keur publicatie goed.",
    "admin.libcms.confirmPublish": "Goedkeuren & publiceren",
    "admin.libcms.cancel": "Annuleren",
    "admin.libcms.publishBlocked": "Publicatie geblokkeerd. Tweetaligheid onvolledig:",
    "admin.libcms.applySeed": "Inhoudsupdate toepassen",
    "admin.library.intro": "De Bibliotheek is de referentielaag van StoicOs.ai: boeken, citaten, bronnen en later ook AI-geïndexeerde embeddings. UI is voorbereid; backend-endpoints worden later ingevoegd zonder verdere layoutwijzigingen.",
    "admin.library.books": "Boeken",
    "admin.library.books.desc": "Fysieke en digitale bibliotheek. Omslag, auteur, notities, tags.",
    "admin.library.readingLists": "Leeslijsten",
    "admin.library.readingLists.desc": "Samengestelde routes (Beginnende Stoïcijn, Marcus in de diepte, enz.).",
    "admin.library.quotes": "Citaten",
    "admin.library.quotes.desc": "Geattribueerde citatenbank gekoppeld aan boeken en vertalingen.",
    "admin.library.sources": "Historische bronnen",
    "admin.library.sources.desc": "Primaire bronnen: Meditaties, Discourses, Brieven.",
    "admin.library.pdfs": "Geüploade pdf's",
    "admin.library.pdfs.desc": "Referentie-pdf's voor persoonlijke studie, met doorzoekbare tekstlaag.",
    "admin.library.search": "Zoeken",
    "admin.library.search.desc": "Full-text zoeken door de gehele bibliotheek.",
    "admin.library.categories": "Categorieën",
    "admin.library.categories.desc": "Deugd · Emotie · Praktijk · Filosofie · Tijdperk.",
    "admin.library.collections": "Collecties",
    "admin.library.collections.desc": "Handmatig samengestelde bundels die op de publieke site verschijnen.",
    "admin.library.aiIndex": "AI-indexering",
    "admin.library.aiIndex.desc": "Semantische embeddings voor retrieval-augmented antwoorden.",

    // ---------- Messages ----------
    "admin.messages.title": "Berichten",
    "admin.messages.intro": "Elke reflectie die vanaf \u201cShare Your Thoughts\u201d op de publieke site wordt verstuurd, komt hier binnen. Antwoordfunctionaliteit staat op de roadmap.",
    "admin.messages.stats.total": "Totaal berichten",
    "admin.messages.stats.recent": "Afgelopen 7 dagen",
    "admin.messages.stats.unread": "Ongelezen",
    "admin.messages.stats.unread.hint": "Antwoord-UI komt eraan",
    "admin.messages.search": "Zoek op naam, e-mail of tekst…",
    "admin.messages.name": "Naam",
    "admin.messages.email": "E-mail",
    "admin.messages.subject": "Onderwerp",
    "admin.messages.message": "Bericht",
    "admin.messages.received": "Ontvangen",
    "admin.messages.reply": "Antwoorden",
    "admin.messages.from": "Van",
    "admin.messages.selectPrompt": "Selecteer een bericht om het te lezen.",
    "admin.messages.noneMatch": "Geen berichten gevonden.",
    "admin.messages.anonymous": "(anoniem)",
    "admin.messages.noName": "(geen naam)",

    // ---------- Downloads ----------
    "admin.downloads.title": "Downloads",
    "admin.downloads.intro": "Distributietelemetrie voor de Virtue Guide-app op iOS & Android. De getallen worden gevuld zodra de rapportage van App Store Connect / Play Console is aangesloten.",
    "admin.downloads.total": "Totaal installaties",
    "admin.downloads.week": "Afgelopen 7 dagen",
    "admin.downloads.ios": "iOS",
    "admin.downloads.android": "Android",
    "admin.downloads.appstoreLive": "Live · Compliance 3.1.1 goedgekeurd.",
    "admin.downloads.playSoon": "Live · Officiële Google Play-badge actief op de publieke site.",
    "admin.downloads.version": "Versie",
    "admin.downloads.lastRelease": "Laatste release",

    // ---------- Settings ----------
    "admin.settings.title": "Instellingen",
    "admin.settings.intro": "Configuratiecentrum. Geheime waarden zijn nooit zichtbaar. Ze worden weergegeven als •••••••• en kunnen alleen worden geroteerd in de productieomgeving.",
    "admin.settings.general": "Algemeen",
    "admin.settings.general.sub": "Site-identiteit en primair domein.",
    "admin.settings.publicDomain": "Publiek domein",
    "admin.settings.apiBase": "API Base",
    "admin.settings.auth": "Authenticatie",
    "admin.settings.auth.sub": "Admin bearer-token dat wordt gebruikt bij elke /api/admin-aanroep.",
    "admin.settings.ai": "AI-providers",
    "admin.settings.ai.sub": "Modelintegraties. Sleutels worden uitsluitend server-side bewaard.",
    "admin.settings.apis": "API-sleutels",
    "admin.settings.apis.sub": "Externe integraties.",
    "admin.settings.autoseo": "AutoSEO",
    "admin.settings.autoseo.sub": "Artikelinvoer via webhook.",
    "admin.settings.env": "Omgeving",
    "admin.settings.env.sub": "Deploy- en runtime-status.",
    "admin.settings.flags": "Feature-flags",
    "admin.settings.flags.sub": "Experimentele onderdelen aan- of uitzetten (voorlopig alleen UI).",
    "admin.settings.journal": "Journaal",
    "admin.settings.journal.sub": "Publicatie-standaarden.",
    "admin.settings.analyticsFlags": "Analyse",
    "admin.settings.analyticsFlags.sub": "Welke onderdelen events versturen.",
    "admin.settings.downloadsFlags": "Downloads",
    "admin.settings.downloadsFlags.sub": "Distributie van de Virtue Guide-app.",
    "admin.settings.language": "Taal",
    "admin.settings.theme": "Thema",
    "admin.settings.security": "Beveiliging",
    "admin.settings.website": "Website",
    "admin.settings.seo": "SEO",

    // ---------- System ----------
    "admin.system.title": "Systeem",
    "admin.system.intro": "Realtime status van subsystemen. Vernieuwen voert een geauthenticeerde ping uit tegen de admin-API en rapporteert de status van elk subsysteem.",
    "admin.system.api": "API",
    "admin.system.db": "Database",
    "admin.system.storage": "Opslag",
    "admin.system.webhook": "AutoSEO-webhook",
    "admin.system.uptime": "Sessieduur",
    "admin.system.env": "Omgeving",
    "admin.system.version": "Versie",
    "admin.system.logs": "Logboek",
    "admin.system.lastCheck": "Laatste controle",
    "admin.system.region": "Regio",
    "admin.system.frontend": "Frontend",
    "admin.system.backend": "Backend",

    // ---------- Notifications ----------
    "admin.notif.title": "Notificaties",
    "admin.notif.none": "Nog geen notificaties.",
    "admin.notif.published": "Journaalartikel gepubliceerd",
    "admin.notif.updated": "Artikel bijgewerkt",
    "admin.notif.autoseo": "SEO-artikel wacht",
    "admin.notif.message": "Nieuw contactbericht",
    "admin.notif.system": "Systeemmelding",

    // ---------- Confirmations ----------
    "admin.confirm.delete": "\u201c{title}\u201d permanent verwijderen?",
    "admin.confirm.deleteShort": "Artikel verwijderen?",
    "admin.inbox.title": "Content Postvak",
    "admin.inbox.subtitle": "Alles wat vandaag uw aandacht vraagt.",
    "admin.inbox.refresh": "Vernieuwen",
    "admin.inbox.counter.newToday": "Vandaag nieuw",
    "admin.inbox.counter.drafts": "Concepten",
    "admin.inbox.counter.waitingNl": "Wacht op NL",
    "admin.inbox.counter.readyToPublish": "Klaar om te publiceren",
    "admin.inbox.counter.publishedToday": "Vandaag gepubliceerd",
    "admin.inbox.source.autoseo": "AutoSEO",
    "admin.inbox.source.manual": "Handmatig",
    "admin.inbox.status.draft": "Concept",
    "admin.inbox.status.published": "Gepubliceerd",
    "admin.inbox.action.review": "Bekijken",
    "admin.inbox.action.edit": "Bewerken",
    "admin.inbox.action.translate": "Vertaal naar Nederlands",
    "admin.inbox.action.publish": "Publiceren",
    "admin.inbox.action.unpublish": "Depubliceren",
    "admin.inbox.action.delete": "Verwijderen",
    "admin.inbox.action.more": "Meer acties",
    "admin.inbox.empty.title": "Alles is up-to-date.",
    "admin.inbox.empty.body": "De wijze onderhoudt zijn werk voordat hij meer zoekt.",
    "admin.inbox.time.now": "zojuist",
    "admin.inbox.time.mins": "{n} min geleden",
    "admin.inbox.time.hrs": "{n} u geleden",
    "admin.inbox.time.days": "{n} d geleden",
    "admin.lexicon.eyebrow": "Ecosysteem",
    "admin.lexicon.title": "StoicOs Lexicon",
    "admin.lexicon.subtitle": "De enige bron van waarheid voor elke AI-workflow: vertaling, AutoSEO, Holo AI-campagnes, agents, SEO, sociaal. Beschermde termen worden nooit vertaald of gewijzigd in welke taal dan ook.",
    "admin.lexicon.stat.total": "Totaal aantal termen",
    "admin.lexicon.stat.enabled": "Actief",
    "admin.lexicon.stat.brand": "Merk",
    "admin.lexicon.stat.philosophy": "Filosofie",
    "admin.lexicon.tab.all": "Alle",
    "admin.lexicon.cat.brand": "Merk",
    "admin.lexicon.cat.philosophy": "Filosofie",
    "admin.lexicon.cat.historical": "Historisch",
    "admin.lexicon.searchPlaceholder": "Zoek in termen, aliassen, notities…",
    "admin.lexicon.empty": "Geen termen voldoen aan uw filter.",
    "admin.lexicon.disabled": "Uitgeschakeld",
    "admin.lexicon.aliases": "Aliassen",
    "admin.lexicon.action.new": "Nieuwe term",
    "admin.lexicon.action.import": "JSON importeren",
    "admin.lexicon.action.export": "JSON exporteren",
    "admin.lexicon.action.enable": "Bescherming inschakelen",
    "admin.lexicon.action.disable": "Bescherming uitschakelen",
    "admin.lexicon.field.term": "Term (canonieke vorm)",
    "admin.lexicon.field.category": "Categorie",
    "admin.lexicon.field.aliases": "Aliassen",
    "admin.lexicon.field.aliasesHint": "Alternatieve schrijfwijzen die ook beschermd moeten worden. Gescheiden door komma's.",
    "admin.lexicon.field.notes": "Notities (alleen intern)",
    "admin.lexicon.field.enabled": "Bescherming actief",
    "admin.lexicon.editor.new": "Nieuwe Lexicon-term",
    "admin.lexicon.editor.edit": "Lexicon-term bewerken",
    "admin.lexicon.err.termRequired": "Term is verplicht.",
    "admin.lexicon.msg.saved": "Term opgeslagen. Bescherming is actief bij de volgende AI-oproep.",
    "admin.lexicon.msg.created": "Term toegevoegd aan het Lexicon.",
    "admin.lexicon.msg.deleted": "\u201c{term}\u201d verwijderd.",
    "admin.lexicon.msg.exported": "{n} termen ge\u00ebxporteerd als JSON.",
    "admin.lexicon.msg.imported": "Ge\u00efmporteerd: {inserted} toegevoegd, {skipped} duplicaten, {invalid} afgewezen.",
    "admin.lexicon.confirm.delete": "\u201c{term}\u201d verwijderen uit het Lexicon? Het wordt niet meer beschermd in AI-gegenereerde inhoud.",
    "admin.lexicon.help": "Elke AI-vertaling, AutoSEO-artikel, toekomstige Holo AI-campagne en Founder Portal-agent gebruikt dit Lexicon voordat er tekst wordt gegenereerd. Bewerkingen zijn direct van kracht (de backend-cache wordt bij elke opslag vernieuwd).",
    "admin.vault.eyebrow": "Fundament",
    "admin.vault.title": "Content Kluis",
    "admin.vault.tagline": "\u00c9\u00e9n keer maken. Voor altijd bewaren. Overal hergebruiken.",
    "admin.vault.stat.total": "Assets opgeslagen",
    "admin.vault.upload.title": "Sleep afbeeldingen hierheen",
    "admin.vault.upload.subtitle": "Of selecteer meerdere bestanden. Elk asset krijgt een permanente Content ID (SW-000001).",
    "admin.vault.upload.formats": "JPG · PNG · WEBP · PDF · max {max} MB per bestand",
    "admin.vault.upload.button": "Bestanden kiezen",
    "admin.vault.tab.all": "Alle",
    "admin.vault.tab.draft": "Concept",
    "admin.vault.tab.published": "Gepubliceerd",
    "admin.vault.tab.archived": "Gearchiveerd",
    "admin.vault.searchPlaceholder": "Zoek op titel, Content ID of originele bestandsnaam\u2026",
    "admin.vault.loadMore": "Meer laden",
    "admin.vault.empty.title": "Nog niets hier.",
    "admin.vault.empty.body": "Elk origineel stukje kennis: \u00e9\u00e9n keer gemaakt, voor altijd bewaard, overal hergebruikt.",
    "admin.vault.field.title": "Titel",
    "admin.vault.field.author": "Auteur",
    "admin.vault.field.uploaded": "Ge\u00fcpload",
    "admin.vault.field.filename": "Originele bestandsnaam",
    "admin.vault.field.type": "Bestandstype",
    "admin.vault.field.size": "Grootte",
    "admin.vault.field.dims": "Afmetingen",
    "admin.vault.field.status": "Status",
    "admin.vault.action.viewOriginal": "Origineel bekijken",
    "admin.vault.confirm.delete": "\u201c{title}\u201d verwijderen? Het asset wordt zacht verwijderd (originele bytes blijven bewaard in de opslag).",
    "admin.vault.readiness.ready":               "Klaar voor Site Media",
    "admin.vault.readiness.no_public_url":       "Geen publieke URL",
    "admin.vault.readiness.upload_incomplete":   "Upload onvolledig",
    "admin.vault.readiness.storage_unreachable": "Opslag onbereikbaar",
    "admin.vault.readiness.generic":             "Nog niet klaar",
    "admin.vault.readiness.sectionTitle":        "Publicatie-check",
    "admin.vault.readiness.check.has_content_id":    "Permanent Content ID",
    "admin.vault.readiness.check.has_storage_path":  "Opslagpad ingesteld",
    "admin.vault.readiness.check.has_file_size":     "Bestandsgrootte bekend",
    "admin.vault.readiness.check.upload_complete":   "Upload afgerond",
    "admin.vault.readiness.check.storage_reachable": "Bytes bereikbaar",
    "admin.vault.readiness.verify":              "Verifieer publicatie-check",
    "admin.vault.readiness.verifying":           "Bezig met verifi\u00ebren\u2026",
    "admin.vault.readiness.publicUrl":           "Publieke URL",
    "admin.vault.readiness.publishBlocked":      "Publiceren geblokkeerd: {reason}",
    "admin.vault.ai.title": "AI Verrijking",
    "admin.vault.ai.intro": "Analyseer dit asset met Claude Vision. Haal OCR, tags, deugden en tweetalige metadata op. Er wordt niets opgeslagen tot je bevestigt.",
    "admin.vault.ai.analyze": "Analyseer met AI",
    "admin.vault.ai.reanalyze": "Opnieuw analyseren",
    "admin.vault.ai.confirmed": "AI bevestigd",
    "admin.vault.ai.working": "Bezig met analyseren\u2026",
    "admin.vault.ai.workingHint": "OCR + metadata-extractie. Dit kan 20\u201360 seconden duren voor PDF\u2019s.",
    "admin.vault.ai.proposal.title": "AI Voorstel",
    "admin.vault.ai.proposal.hint": "Bewerk alles waar je het niet mee eens bent. Er wordt niets opgeslagen tot je op Bevestigen drukt.",
    "admin.vault.ai.proposal.brandProtected": "Merktermen beschermd via Lexicon",
    "admin.vault.ai.proposal.promoteTitle": "Vervang ook de hoofdtitel van het asset met de Engelse AI-titel",
    "admin.vault.ai.proposal.confirm": "Bevestigen & Opslaan",
    "admin.vault.ai.proposal.discard": "Negeren",
    "admin.vault.ai.proposal.page": "pagina",
    "admin.vault.ai.proposal.pages": "pagina\u2019s",
    "admin.vault.ai.field.titleEn": "Titel (EN)",
    "admin.vault.ai.field.titleNl": "Titel (NL)",
    "admin.vault.ai.field.descriptionEn": "Beschrijving (EN)",
    "admin.vault.ai.field.descriptionNl": "Beschrijving (NL)",
    "admin.vault.ai.field.detectedLanguage": "Gedetecteerde taal",
    "admin.vault.ai.field.tags": "Tags",
    "admin.vault.ai.field.themes": "Thema\u2019s",
    "admin.vault.ai.field.virtues": "Sto\u00efsche deugden",
    "admin.vault.ai.field.emotions": "Emoties",
    "admin.vault.ai.field.ocr": "OCR-transcriptie",
    "admin.vault.bulk.button": "Analyseer alle onverwerkt",
    "admin.vault.bulk.buttonHint": "Analyseer in \u00e9\u00e9n keer elk asset dat nog geen bevestigde AI-verrijking heeft.",
    "admin.vault.bulk.confirmTitle": "Bulk AI-Verrijking",
    "admin.vault.bulk.confirmBody": "Je staat op het punt {n} onverwerkte asset(s) te analyseren met Claude Vision. Elk asset duurt 5\u201330 s. Voorstellen worden automatisch opgeslagen als \u201cbevestigd (bulk)\u201d zodat downstream hergebruik werkt. Je kunt achteraf nog altijd bewerken of opnieuw analyseren.",
    "admin.vault.bulk.confirmWarning": "Anders dan bij enkelvoudige analyse stopt de bulk-modus NIET voor handmatige review. Als je elk voorstel wilt beoordelen, sluit dit dialoog en analyseer assets \u00e9\u00e9n voor \u00e9\u00e9n.",
    "admin.vault.bulk.start": "Start batch",
    "admin.vault.bulk.progressTitle": "Bulk AI-Verrijking · Voortgang",
    "admin.vault.bulk.processed": "verwerkt",
    "admin.vault.bulk.done": "Klaar",
    "admin.vault.bulk.failed": "Mislukt",
    "admin.vault.bulk.skipped": "Overgeslagen",
    "admin.vault.bulk.log": "Live log",
    "admin.vault.bulk.logEmpty": "Wachten op de worker\u2026",
    "admin.vault.bulk.cancelJob": "Job annuleren",
    "admin.vault.bulk.closeDisabled": "Job loopt nog. Annuleer eerst als je wilt stoppen.",
    "admin.vault.action.replace": "Bytes vervangen",
    "admin.vault.replace.confirm": "De bytes van dit asset vervangen door het nieuwe bestand? De Content ID blijft hetzelfde en alle consumers pikken het nieuwe bestand automatisch op. Dit kan niet ongedaan gemaakt worden.",
    "admin.vault.usedIn.title": "Gebruikt In",
    "admin.vault.usedIn.loading": "Gebruik controleren\u2026",
    "admin.vault.usedIn.empty": "Nog nergens gebruikt.",
    "admin.vault.usedIn.summary": "Gebruikt op {n} plek(ken).",
    "admin.vault.integrity.check": "Integriteit controleren",
    "admin.vault.integrity.ok": "Bytes intact",
    "admin.vault.integrity.fail": "Bytes mismatch",
    "admin.confirm.publish": "Artikel publiceren?",
    "admin.confirm.save": "Wijzigingen opslaan?",
    "admin.confirm.cancelEdit": "Bewerken annuleren?",

    // ---------- Publishing Studio ----------
    "pubstudio.title": "Publishing Studio",
    "pubstudio.tab.journal":    "Journaal",
    "pubstudio.tab.social":     "Social",
    "pubstudio.tab.newsletter": "Newsletter",
    "pubstudio.tab.analytics":  "Analyse",

    "pubstudio.intro.journal":    "Elk gepubliceerd artikel, klaar om verspreid te worden. Open er één om te beginnen.",
    "pubstudio.intro.social":     "Stel platform-specifieke berichten op die lezers altijd terug naar stoicos.ai brengen.",
    "pubstudio.intro.newsletter": "Stel een weekoverzicht samen uit uw gepubliceerde artikelen.",
    "pubstudio.intro.analytics":  "Live-tellers van uw publicatie-activiteit. Verkeer- en lezersstatistieken volgen zodra de analytics-integratie live is.",

    "pubstudio.loading":      "Laden\u2026",
    "pubstudio.back":         "Terug naar Journaal",
    "pubstudio.generate":     "Genereren",
    "pubstudio.regenerate":   "Opnieuw genereren",
    "pubstudio.generating":   "Bezig met genereren\u2026",

    "pubstudio.empty.title": "Nog geen gepubliceerde artikelen.",
    "pubstudio.empty.body":  "Publiceer een artikel in het Journal Command Center en het verschijnt hier.",

    "pubstudio.card.open":     "Openen",
    "pubstudio.card.generate": "Genereren",
    "pubstudio.card.min":      "min",
    "pubstudio.card.posts":    "berichten",

    "pubstudio.social.forArticle":     "Voor artikel",
    "pubstudio.linkedin.imageChecking": "LinkedIn-previewafbeelding controleren…",
    "pubstudio.linkedin.imageReady":    "LinkedIn-previewafbeelding gereed",
    "pubstudio.linkedin.noImage":       "Geen LinkedIn-afbeelding gekoppeld.",
    "pubstudio.social.preview":        "Voorbeeld",
    "pubstudio.social.notGenerated":   "Nog niet gegenereerd",
    "pubstudio.social.lastGenerated":  "Laatst gegenereerd",
    "pubstudio.social.emptyBody":      "Er is nog niets gegenereerd voor dit platform.",
    "pubstudio.social.emptyHint":      "Druk op Genereren om een bericht in Eds stem op te stellen.",
    "pubstudio.social.pickTitle":      "Kies een artikel om te beginnen.",
    "pubstudio.social.pickBody":       "Ga terug naar het Journaal-tabblad en kies een artikel, of selecteer er hieronder één.",

    "pubstudio.newsletter.eyebrow":   "Weekoverzicht",
    "pubstudio.newsletter.title":     "Stel de nieuwsbrief van deze week samen.",
    "pubstudio.newsletter.body":      "Selecteer de artikelen die u wilt uitlichten. Het overzicht wordt geschreven als een persoonlijke brief van de oprichter, klaar om in Substack of een e-mailplatform te plakken.",
    "pubstudio.newsletter.pick":      "Opgenomen artikelen",
    "pubstudio.newsletter.selected":  "geselecteerd",
    "pubstudio.newsletter.generate":  "Overzicht genereren",
    "pubstudio.newsletter.digest":    "Weekoverzicht",
    "pubstudio.newsletter.field.subject": "Onderwerp",
    "pubstudio.newsletter.field.intro":   "Introductie",
    "pubstudio.newsletter.field.main":    "Hoofdartikel",
    "pubstudio.newsletter.field.closing": "Slotreflectie",
    "pubstudio.newsletter.field.cta":     "Oproep tot actie",

    "pubstudio.analytics.articlesPublished":  "Gepubliceerde artikelen",
    "pubstudio.analytics.linkedinPosts":      "LinkedIn-berichten",
    "pubstudio.analytics.substackPosts":      "Substack-berichten",
    "pubstudio.analytics.totalGenerated":     "Totaal gegenereerd",
    "pubstudio.analytics.byPlatform":         "Gegenereerd per platform",
    "pubstudio.analytics.mostGenerated":      "Meest verspreide artikelen",
    "pubstudio.analytics.posts":              "berichten",
    "pubstudio.analytics.noData":             "Nog geen distributieberichten gegenereerd.",
    "pubstudio.analytics.trafficSources":     "Verkeersbronnen",
    "pubstudio.analytics.readingTime":        "Gemiddelde leestijd",
    "pubstudio.analytics.ctrVirtueGuide":     "CTR naar Virtue Guide",
    "pubstudio.analytics.pendingIntegration": "Beschikbaar na GA / Clarity-integratie",
  },
};
